// Pure helpers for the billing page's staged promo-code discount preview.
// Extracted from PromoCodeSection so the discounted-price math can be unit
// tested in isolation — a regression here would silently mislead users about
// pricing before they start Stripe Checkout.

export type DiscountType = "percent" | "fixed";

// The monthly-equivalent gross for the selected plan/cycle. Annual plans are
// always shown per-month (annualTotal / 12) so the preview is comparable.
export function computeGrossMonthly(summary: {
  cycle: string;
  annualTotal: number;
  monthlyTotal: number;
}): number {
  return summary.cycle === "annual" ? summary.annualTotal / 12 : summary.monthlyTotal;
}

// The discounted monthly-equivalent price after applying a promo. Returns null
// when the discount can't be meaningfully applied (non-positive/invalid value
// or a zero gross), in which case the UI shows no struck-through price.
export function computeDiscountedMonthly(
  discountType: DiscountType,
  discountValue: string | number,
  grossMonthly: number,
): number | null {
  const val = Number(discountValue);
  if (!(Number.isFinite(val) && val > 0 && grossMonthly > 0)) return null;
  return discountType === "percent"
    ? Math.max(0, Math.round(grossMonthly * (1 - val / 100) * 100) / 100)
    : Math.max(0, Math.round((grossMonthly - val) * 100) / 100);
}
